import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { business } from "@/config/site";

const links = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Packages", id: "packages" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-[9995] transition-colors duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-champagne/20 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex flex-col items-start leading-none"
        >
          <span
            className={`font-display text-2xl italic tracking-tight transition-colors duration-500 ${
              scrolled ? "text-maroon" : "text-white"
            }`}
          >
            {business.name}
          </span>
          <span
            className={`text-[9px] tracking-[0.25em] uppercase mt-0.5 transition-colors duration-500 ${
              scrolled ? "text-charcoal/60" : "text-white/70"
            }`}
          >
            Events & Celebrations
          </span>
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`group relative text-sm tracking-wide transition-colors duration-300 ${
                scrolled ? "text-charcoal hover:text-maroon" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-champagne transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-book-btn"
            onClick={onBook}
            className="rounded-full bg-champagne px-6 py-2.5 text-sm font-medium text-charcoal transition-all duration-300 hover:bg-maroon hover:text-white"
          >
            Book Consultation
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden ${scrolled ? "text-maroon" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden bg-white/95 backdrop-blur-xl border-b border-champagne/20 lg:hidden"
          >
            <div className="flex flex-col px-6 py-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="border-b border-champagne/10 py-3 text-left text-charcoal hover:text-maroon"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-book-btn"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="mt-4 rounded-full bg-champagne py-3 text-center font-medium text-charcoal"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
